import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { Wallet as WalletIcon, Landmark, ArrowDownToLine, Plus, Clock, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);
  const [bank, setBank] = useState({ account_holder: "", bank_name: "", account_number: "", routing_number: "" });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    api.get("/wallet/withdrawals").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connectBank = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/bank", bank);
      toast.success("Bank account connected (test).");
      setBankOpen(false);
      load();
    } catch (e) { toast.error(apiError(e, "Could not connect bank")); }
    finally { setBusy(false); }
  };

  const addFunds = async () => {
    try {
      const { data } = await api.post("/wallet/add-test-funds");
      setWallet(data);
      toast.success("Added $250 test funds.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doWithdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/withdraw", { amount: Number(amount) });
      setWallet(data.wallet);
      toast.success(`Withdrawal of ${money(data.withdrawal.amount)} initiated (test).`);
      setWdOpen(false); setAmount("");
      load();
    } catch (e) { toast.error(apiError(e, "Withdrawal failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Wallet & Payouts" subtitle="Track your balance and withdraw earnings to your bank. Running in TEST mode — no real money moves until Stripe is live." testid="wallet-header">
        <Pill tone="amber">Test Mode</Pill>
      </PageHeader>

      <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2.5">
        <ArrowDownToLine className="w-4 h-4 mt-0.5 shrink-0" />
        <span>MOCKED: balances are credited from your tracked net profit and withdrawals are simulated. Real payouts to your bank happen automatically via Stripe once you claim &amp; verify your Stripe account and have live sales.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2 border-emerald-500/20 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs text-slate-400 font-mono uppercase tracking-wider">Available to withdraw</div>
              <div className="mt-2 text-4xl font-head font-extrabold text-emerald-400" data-testid="wallet-available">{money(wallet?.available)}</div>
              <div className="mt-3 flex gap-4 text-sm">
                <span className="text-slate-400">Pending <span className="text-amber-300 font-medium">{money(wallet?.pending)}</span></span>
                <span className="text-slate-400">Withdrawn <span className="text-slate-200 font-medium">{money(wallet?.withdrawn)}</span></span>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="add-test-funds-button" onClick={addFunds} className="px-3 py-2 rounded-lg bg-slate-800/70 text-slate-200 text-sm hover:bg-slate-700 border border-slate-700/60 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add test funds</button>
              <button data-testid="open-withdraw-button" onClick={() => { if (!wallet?.bank) { toast.error("Connect a bank account first."); setBankOpen(true); } else { setWdOpen(true); } }} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 flex items-center gap-1.5"><ArrowDownToLine className="w-4 h-4" /> Withdraw</button>
            </div>
          </div>
        </Card>

        <Card className="p-6" data-testid="bank-card">
          <div className="flex items-center gap-2 mb-3"><Landmark className="w-5 h-5 text-emerald-400" /><h3 className="font-head text-base font-semibold text-slate-100">Bank account</h3></div>
          {wallet?.bank ? (
            <div className="space-y-1 text-sm">
              <div className="text-slate-200 font-medium">{wallet.bank.bank_name}</div>
              <div className="text-slate-400">{wallet.bank.account_holder}</div>
              <div className="text-slate-400 font-mono">•••• {wallet.bank.last4}</div>
              <button data-testid="change-bank-button" onClick={() => setBankOpen(true)} className="mt-3 text-xs text-emerald-400 hover:underline">Change bank</button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-400">No bank connected. Add one to enable withdrawals.</p>
              <button data-testid="connect-bank-button" onClick={() => setBankOpen(true)} className="mt-3 w-full px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400">Connect bank</button>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6" data-testid="withdrawals-table">
        <h3 className="font-head text-lg font-medium text-slate-200 mb-4">Withdrawal history</h3>
        {history.length === 0 ? (
          <EmptyState icon={WalletIcon} title="No withdrawals yet" hint="Connect a bank and withdraw your available balance." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 font-mono uppercase tracking-wider border-b border-slate-800">
                  <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">To</th><th className="py-2 pr-4">ETA</th><th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((w) => (
                  <tr key={w.id} className="border-b border-slate-800/60" data-testid={`withdrawal-row-${w.id}`}>
                    <td className="py-3 pr-4 text-slate-400">{new Date(w.created_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-4 text-emerald-400 font-medium">{money(w.amount)}</td>
                    <td className="py-3 pr-4 text-slate-300 font-mono">•••• {w.bank_last4}</td>
                    <td className="py-3 pr-4 text-slate-400">{w.est_arrival}</td>
                    <td className="py-3 pr-4">
                      <Pill tone={w.status === "paid" ? "emerald" : "amber"}>
                        {w.status === "paid" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {w.status}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">Connect bank account</DialogTitle>
            <DialogDescription className="text-slate-400">Test mode — use any values. No real bank is contacted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="bank-holder-input" value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} placeholder="Account holder name" className="w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
            <input data-testid="bank-name-input" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Bank name (optional)" className="w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
            <input data-testid="bank-account-input" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} placeholder="Account number" className="w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm font-mono" />
            <input data-testid="bank-routing-input" value={bank.routing_number} onChange={(e) => setBank({ ...bank, routing_number: e.target.value })} placeholder="Routing number" className="w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm font-mono" />
          </div>
          <DialogFooter>
            <button data-testid="save-bank-button" onClick={connectBank} disabled={busy} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-60">{busy ? "Saving…" : "Connect bank"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={wdOpen} onOpenChange={setWdOpen}>
        <DialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">Withdraw to bank</DialogTitle>
            <DialogDescription className="text-slate-400">Available: {money(wallet?.available)} · to •••• {wallet?.bank?.last4}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus-within:border-emerald-500/60">
            <span className="text-slate-500">$</span>
            <input data-testid="withdraw-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="flex-1 bg-transparent outline-none text-sm" />
            <button type="button" onClick={() => setAmount(String(wallet?.available || 0))} className="text-xs text-emerald-400 hover:underline">Max</button>
          </div>
          <DialogFooter>
            <button data-testid="confirm-withdraw-button" onClick={doWithdraw} disabled={busy} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-60">{busy ? "Processing…" : "Withdraw"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
